import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  TextField,
  Button,
} from '@mui/material';
import { DateField, SmartSelect, type SmartSelectOption } from '../../dropdown';
import type { CustomerServiceStatus } from '@techgitter/contracts';
import type { CustomerServiceUpdateRequest } from '@techgitter/contracts';
import { CUSTOMER_SERVICE_STATUS_OPTIONS } from './customerServiceStatusOptions';
import { FIELD_SX, PRIMARY_BTN_SX } from '../../../styles/designSystem';
import './CustomerServiceDrawer.css';

interface CustomerServiceInlineEditorProps {
  service: CustomerServiceStatus;
  canEdit: boolean;
  onClose: () => void;
  onSave: (customerServiceId: string, updates: CustomerServiceUpdateRequest) => Promise<void>;
}

function toInputDate(value?: string | null): string {
  if (!value) return '';
  const isoDate = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  if (isoDate) return isoDate[1];
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
}

export const CustomerServiceInlineEditor: React.FC<CustomerServiceInlineEditorProps> = ({
  service,
  canEdit,
  onClose,
  onSave,
}) => {
  const [notes, setNotes] = useState(service.notesFull || '');
  const [status, setStatus] = useState<number>(service.status?.value ?? 0);
  const [onboardingDate, setOnboardingDate] = useState<string>(toInputDate(service.onboardingDate));
  const [startDate, setStartDate] = useState<string>(toInputDate(service.startDate));
  const [isSaving, setIsSaving] = useState(false);
  const startDateManuallySet = useRef(false);

  const statusOptions = useMemo(() => {
    const hasCurrent = CUSTOMER_SERVICE_STATUS_OPTIONS.some((option) => option.value === status);
    if (hasCurrent) return CUSTOMER_SERVICE_STATUS_OPTIONS;
    return [{ value: status, label: service.status?.label ?? `Status ${status}` }, ...CUSTOMER_SERVICE_STATUS_OPTIONS];
  }, [service.status?.label, status]);

  // SmartSelect speaks strings; the status value is numeric.
  const statusSelectOptions: SmartSelectOption[] = useMemo(
    () => statusOptions.map((o) => ({ value: String(o.value), label: o.label })),
    [statusOptions],
  );

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updates: CustomerServiceUpdateRequest = {
        status,
        noteText: notes.trim() ? notes.trim() : null,
        onboardingDate: onboardingDate || null,
        startDate: startDate || null,
      };
      await onSave(service.customerServiceId, updates);
      onClose();
    } catch (error) {
      console.error('Failed to save service:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      className="service-inline-editor"
      sx={{
        mx: 2,
        mt: '4px',
        mb: '4px',
        bgcolor: '#ffffff',
        border: '0.5px solid #e2eaf3',
        borderRadius: '8px',
        boxShadow: '0 0.5px 2px rgba(0, 0, 0, 0.05)',
      }}
    >
      {/* Padded content container — keeps every field inset from the card edge. */}
      <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
      <Box sx={{ width: '100%' }}>
        <SmartSelect
          label="Status"
          value={String(status)}
          options={statusSelectOptions}
          onChange={(v) => { if (v != null) setStatus(Number(v)); }}
          disabled={!canEdit}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <DateField
            label="Onboarding Date"
            value={onboardingDate || null}
            onChange={(iso) => {
              const newOnboardingDate = iso ?? '';
              setOnboardingDate(newOnboardingDate);
              if (!newOnboardingDate) return;
              if (!startDateManuallySet.current || !startDate || startDate < newOnboardingDate) {
                setStartDate(newOnboardingDate);
              }
            }}
            disabled={!canEdit}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <DateField
            label="Start Date"
            value={startDate || null}
            onChange={(iso) => {
              setStartDate(iso ?? '');
              startDateManuallySet.current = true;
            }}
            disabled={!canEdit}
          />
        </Box>
      </Box>

      <TextField
        fullWidth
        multiline
        rows={4}
        label="Notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        disabled={!canEdit}
        sx={[FIELD_SX, { mb: 1 }]}
      />

      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Button
          onClick={onClose}
          size="small"
          sx={{ color: '#aaaaaa', textTransform: 'none', '&:hover': { color: '#555555', bgcolor: 'transparent' } }}
        >
          Cancel
        </Button>
        {canEdit && (
          <Button
            onClick={handleSave}
            variant="contained"
            size="small"
            disabled={isSaving}
            sx={PRIMARY_BTN_SX}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </Box>
      </Box>
    </Box>
  );
};
